import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Stack,
  Typography,
  Autocomplete,
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as yup from "yup";
import { string } from "yup";
import { AutoCompleteOption } from "../common/AutoComplete";
import { statusReasonActions } from "../../service/statusreason.actions";
import useTenant from "../../hooks/useTenant";

// subCode del 409 di vehicle-service (ConfigurationData.STATUS_REASON_NOT_BLOCKABLE_CODE)
const STATUS_REASON_NOT_BLOCKABLE_CODE_SUBCODE = 17;

export interface ValuesStatusReasonObject {
  code?: string;
  descr: string;
  status?: string;
  valid: string;
  id?: string;
  transferable?: string;
  damages?: string;
  blocked?: string;
}

/**
 * Popup di add/edit status reason — `operation/vehicles/statusReasons`.
 *
 * CR LPP-20500 RF-13 — modifica:
 *  - il campo `status` diventa editabile **solo se** l'operatore ha la grant
 *    `SCOPE_AVAILABILITY_STATUS_REASON_MANAGE` (prop `canEditStatus`).
 *    Le reason con `tenant === null` non sono modificabili affatto: la matita in
 *    tabella è già disabilitata (campo `default`), quindi il popup non si apre per esse.
 *  - se l'utente cambia status (NOT_AVAILABLE ↔ AVAILABLE) durante l'edit,
 *    al submit:
 *    1. chiamata `GET /sc-be/availability/statusReason/{code}/impactCount`
 *    2. popup conferma con `impactedVehicleCount`
 *    3. su conferma: `PUT /sc-be/availability/statusReason/{code}/toggle`
 *       (NO update ottimistico, refetch lato chiamante per aggiornare la riga)
 *    4. su errore: stato FE invariato, toast/dialog errore
 */
export const AddNewStatusReasons = (props: any) => {
  const createNewStatusReason = props.createNewStatusReason;
  const getByCodeStatusReason = props.getByCodeStatusReason;
  const { t } = useTranslation(["operation"]);

  // CR LPP-20500: props opzionali (retro-compat — undefined = comportamento legacy).
  const canEditStatus: boolean = Boolean(props.canEditStatus);
  const tenant: string | undefined = props.tenant;
  const originalStatus: string | undefined = props.originalStatus;

  const statusEditable = canEditStatus && Boolean(props.getStatusReasonDataForm.id);

  // CR LPP-20500 (variante v1.6): segregazione per tenant in creazione.
  // Se l'operatore e' multi-tenant si mostra una tendina con i soli tenant di competenza;
  // se e' mono-tenant il tenant assegnato (props.tenant) e' passato implicitamente.
  const { tenantOptions } = useTenant();
  const isCreate = !props.getStatusReasonDataForm.id;
  const isMultiTenant = Boolean(tenantOptions && tenantOptions.length > 1);
  const [selectedTenant, setSelectedTenant] = React.useState<string | undefined>(tenant);

  // Stato del flusso toggle (impact count → confirm → toggle).
  const [impactDialogOpen, setImpactDialogOpen] = React.useState(false);
  const [impactCount, setImpactCount] = React.useState<number | null>(null);
  const [impactInfo, setImpactInfo] = React.useState<{ currentStatus: string; targetStatus: string } | null>(null);
  const [pendingToggleValues, setPendingToggleValues] = React.useState<ValuesStatusReasonObject | null>(null);
  const [toggleLoading, setToggleLoading] = React.useState(false);
  const [toggleError, setToggleError] = React.useState<string | null>(null);
  // Esito dell'operazione toggle (popup successo/errore post-conferma).
  const [resultDialogOpen, setResultDialogOpen] = React.useState(false);
  const [resultSuccess, setResultSuccess] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState<string>("");

  const handleClose = () => {
    props.setStatusReasonDataForm({
      code: "",
      descr: "",
      status: "",
      valid: "",
      transferable: "",
      damages: "",
      blocked: "",
      id: "",
    });
    props.setOpenAddDialog(false);
  };

  const arrayStatusReason = ["AVAILABLE", "NOT_AVAILABLE"];
  const arrayValidityReason = ["true", "false"];

  const transferable: AutoCompleteOption[] = [
    { value: true, label: t("internOptionSelectName.yes") },
    { value: false, label: t("internOptionSelectName.no") },
  ];

  const changeStatusName = (option: any) => {
    switch (option) {
      case "AVAILABLE":
        return t("internOptionSelectName.available");
      case "NOT_AVAILABLE":
        return t("internOptionSelectName.notAvailable");
      default:
        return "";
    }
  };
  const changeValidityName = (option: string) => {
    switch (option) {
      case "true":
        return t("internOptionSelectName.valid");
      case "false":
        return t("internOptionSelectName.inValid");
      default:
        return "";
    }
  };
  const changeTransferableName = (option: string) => {
    switch (option) {
      case "true":
        return t("internOptionSelectName.yes");
      case "false":
        return t("internOptionSelectName.no");
      default:
        return "";
    }
  };

  let validationSchemaStatusReason = {};

  if (!props.getStatusReasonDataForm.id) {
    validationSchemaStatusReason = yup.object({
      code: string()
        .required(t("statusReason.addRecord.requiredCode"))
        .matches(/[^%*$)(\"+&?^~Â`£=!@'_/§#-].{0,}/, t("vehicle.filterModal.regexMessage"))
        .test("Unique Code", t("statusReason.addRecord.testCode"), function (code) {
          return getByCodeStatusReason(code);
        }),
      descr: string()
        .required(t("statusReason.addRecord.requiredDescription"))
        .matches(/[^%*$)(\"+&?^~Â`£=!@'_/§#-].{0,}/, t("vehicle.filterModal.regexMessage")),
      status: string()
        .required(t("statusReason.addRecord.requiredStatus"))
        .typeError(t("statusReason.addRecord.requiredStatus")),
      valid: string()
        .required(t("statusReason.addRecord.requiredValid"))
        .typeError(t("statusReason.addRecord.requiredValid")),
      transferable: string()
        .required(t("statusReason.addRecord.requiredTransferable"))
        .typeError(t("statusReason.addRecord.requiredTransferable")),
      damages: string()
        .required(t("statusReason.addRecord.requiredDamages"))
        .typeError(t("statusReason.addRecord.requiredDamages")),
    });
  } else {
    validationSchemaStatusReason = yup.object({
      descr: string()
        .required(t("statusReason.addRecord.requiredDescription"))
        .matches(/[^%*$)(\"+&?^~Â`£=!@'_/§#-].{0,}/, t("vehicle.filterModal.regexMessage")),
      valid: string()
        .required(t("statusReason.addRecord.requiredValid"))
        .typeError(t("statusReason.addRecord.requiredValid")),
      transferable: string()
        .required(t("statusReason.addRecord.requiredTransferable"))
        .typeError(t("statusReason.addRecord.requiredTransferable")),
      damages: string()
        .required(t("statusReason.addRecord.requiredDamages"))
        .typeError(t("statusReason.addRecord.requiredDamages")),
    });
  }

  const formikStatusReasonObject = useFormik({
    initialValues: {
      code: props.getStatusReasonDataForm.code,
      descr: props.getStatusReasonDataForm.descr,
      status: props.getStatusReasonDataForm.status,
      valid: props.getStatusReasonDataForm.valid.toString(),
      transferable: props.getStatusReasonDataForm.transferable.toString(),
      damages: props.getStatusReasonDataForm.damages.toString(),
      blocked: (props.getStatusReasonDataForm.blocked ?? false).toString(),
      id: props.getStatusReasonDataForm.id ? `${props.getStatusReasonDataForm.id}` : "",
    },
    validationSchema: validationSchemaStatusReason,
    validateOnChange: false,
    onSubmit: (values: ValuesStatusReasonObject) => {
      // CR LPP-20500: detect transizione status su edit → flusso toggle
      const original = originalStatus ?? props.getStatusReasonDataForm.status;
      const statusChanged =
        statusEditable &&
        Boolean(values.id) &&
        values.status &&
        original &&
        values.status !== original;

      if (statusChanged && values.code) {
        setPendingToggleValues(values);
        setToggleError(null);
        // Step 1: chiedi al BE quanti veicoli sono impattati prima di toggle.
        statusReasonActions()
          .getStatusReasonImpactCount(tenant, values.code)
          .then((info) => {
            setImpactCount(info.impactedVehicleCount);
            setImpactInfo({
              currentStatus: info.currentStatus,
              targetStatus: info.targetStatus,
            });
            setImpactDialogOpen(true);
          })
          .catch((err) => {
            setToggleError(
              err?.response?.data?.message ||
                t("statusReason.toggle.impactCountError", "Impossibile recuperare il numero di veicoli impattati. Riprovare.")
            );
            setPendingToggleValues(null);
          });
        return;
      }

      // Flusso normale: salvataggio diretto (campi non-status oppure crea).
      // In creazione passiamo il tenant scelto (multi-tenant) o quello assegnato (mono-tenant).
      if (isCreate) {
        props.createOrEditStatusReason(values, selectedTenant ?? tenant);
      } else {
        props.createOrEditStatusReason(values);
      }
      return handleClose();
    },
  });

  // CR LPP-20500 RF-15: il flag "Bloccato" è editabile solo con grant dedicata
  // (la stessa di RF-13) e solo quando lo status reason corrente è NOT_AVAILABLE.
  const blockedEditable =
    canEditStatus &&
    formikStatusReasonObject.values.status === "NOT_AVAILABLE";

  // ─── Conferma toggle: invoca availability service ──────────────────────
  const onConfirmToggle = () => {
    if (!pendingToggleValues || !pendingToggleValues.code) return;
    const toggledCode = pendingToggleValues.code;
    setToggleLoading(true);
    setToggleError(null);
    statusReasonActions()
      .toggleStatusReason(tenant, toggledCode, true)
      .then(() => {
        setToggleLoading(false);
        setImpactDialogOpen(false);
        // Notifica il chiamante per refetch della riga (no optimistic update).
        if (typeof props.onToggleStatusComplete === "function") {
          props.onToggleStatusComplete(toggledCode);
        }
        // Popup di esito positivo.
        setResultSuccess(true);
        setResultMessage(
          t("statusReason.toggle.successMessage", {
            defaultValue: "Stato della causale {{code}} aggiornato correttamente.",
            code: toggledCode,
          })
        );
        setResultDialogOpen(true);
      })
      .catch((err) => {
        setToggleLoading(false);
        setImpactDialogOpen(false);
        // Popup di esito negativo.
        setResultSuccess(false);
        // LPP-20500 RF-13: codice riservato (RRE/TBC/TBF) non transitabile a NOT AVAILABLE.
        // Il vehicle lo segnala con 409 + subCode, tradotto qui dal dizionario FE.
        const isNotBlockableCode =
          err?.response?.status === 409 &&
          err?.response?.data?.subCode === STATUS_REASON_NOT_BLOCKABLE_CODE_SUBCODE;
        setResultMessage(
          isNotBlockableCode
            ? t(
                "statusReason.toggle.notBlockableCode",
                "La causale non può essere impostata a NOT AVAILABLE perché è un codice riservato"
              )
            : err?.response?.data?.message ||
                t("statusReason.toggle.toggleError", "Errore durante l'aggiornamento dello stato. Riprovare.")
        );
        setResultDialogOpen(true);
      });
  };

  // Chiusura del popup di esito: pulisce lo stato del flusso e, se successo, chiude il form.
  const onCloseResult = () => {
    const wasSuccess = resultSuccess;
    setResultDialogOpen(false);
    setResultMessage("");
    setImpactCount(null);
    setImpactInfo(null);
    setPendingToggleValues(null);
    setToggleError(null);
    if (wasSuccess) {
      handleClose();
    }
  };

  const onCancelToggle = () => {
    if (toggleLoading) return;
    setImpactDialogOpen(false);
    setImpactCount(null);
    setImpactInfo(null);
    setPendingToggleValues(null);
    setToggleError(null);
  };

  return (
    <>
      <Box>
        <form id="statusReasonForm" onSubmit={formikStatusReasonObject.handleSubmit}>
          <Dialog
            fullWidth={true}
            PaperProps={{
              sx: {
                maxWidth: "1161px !important",
                height: "357px",
                borderRadius: "4px",
              },
            }}
            open={props.openAddDialog}
          >
            <DialogTitle>
              <Grid container alignItems="center" alignContent="center" direction="row">
                {props.getStatusReasonDataForm.id ? (
                  <Grid item>{t("statusReason.addRecord.editStatusReason")}</Grid>
                ) : (
                  <Grid item>{t("statusReason.addRecord.newStatusReason")}</Grid>
                )}
                <Grid xs item sx={{ justifyContent: "flex-end", display: "flex" }}>
                  <CloseIcon color="disabled" fontSize="small" onClick={handleClose} />
                </Grid>
              </Grid>
            </DialogTitle>

            <DialogContent sx={{ borderTop: "1px groove rgba(208, 208, 206, 1)" }}>
              <Grid container spacing={2.5} paddingTop={2.5} direction="row" columns={12}>
                {/* CR LPP-20500 (variante v1.6): selezione tenant in creazione, solo se multi-tenant. */}
                {isCreate && isMultiTenant && (
                  <Grid xl={3} lg={3} md={6} xs={12} item>
                    <Box>
                      <Autocomplete
                        id="tenant"
                        disableClearable
                        value={selectedTenant}
                        options={tenantOptions.map((o: AutoCompleteOption) => `${o.value}`)}
                        getOptionLabel={(option) => `${option}`}
                        onChange={(event, value) => setSelectedTenant(value)}
                        includeInputInList
                        renderOption={(p, option) => (
                          <li {...p} key={`${option}`}>
                            {`${option}`}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label={t("statusReason.tenant", "Tenant")}
                            name="tenant"
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                )}
                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <TextField
                      fullWidth
                      id="descr"
                      name="descr"
                      label={t("carRange.description")}
                      value={formikStatusReasonObject.values.descr}
                      onChange={formikStatusReasonObject.handleChange}
                      error={
                        formikStatusReasonObject.touched.descr &&
                        Boolean(formikStatusReasonObject.errors.descr)
                      }
                      helperText={
                        formikStatusReasonObject.touched.descr &&
                        formikStatusReasonObject.errors.descr
                      }
                    />
                  </Box>
                </Grid>
                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <TextField
                      disabled={props.getStatusReasonDataForm.id ? true : false}
                      fullWidth
                      id="code"
                      name="code"
                      label={t("carRange.code")}
                      value={formikStatusReasonObject.values.code}
                      onChange={(e) => {
                        formikStatusReasonObject.setFieldValue(
                          "code",
                          e.currentTarget.value.toUpperCase()
                        );
                      }}
                      error={
                        formikStatusReasonObject.touched.code &&
                        Boolean(formikStatusReasonObject.errors.code)
                      }
                      helperText={
                        formikStatusReasonObject.touched.code &&
                        formikStatusReasonObject.errors.code
                      }
                    />
                  </Box>
                </Grid>
                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <Autocomplete
                      disableClearable
                      // CR LPP-20500 RF-13: campo abilitato in edit solo con grant dedicata.
                      // Le reason tenant=null non arrivano qui (matita disabilitata in tabella).
                      // In create (id assente) resta abilitato come prima.
                      disabled={props.getStatusReasonDataForm.id ? !statusEditable : false}
                      id="status"
                      value={formikStatusReasonObject.values.status}
                      options={arrayStatusReason.map((response) => response)}
                      getOptionLabel={(option) => changeStatusName(option)}
                      onChange={(event, value) => {
                        formikStatusReasonObject.setFieldValue("status", value);
                      }}
                      includeInputInList
                      renderOption={(p, option, state) => (
                        <li {...p} key={p.id}>
                          {changeStatusName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formikStatusReasonObject.touched.status &&
                              formikStatusReasonObject.errors.status
                          )}
                          fullWidth
                          helperText={
                            formikStatusReasonObject.touched.status &&
                            formikStatusReasonObject.errors.status
                          }
                          label={t("statusReason.status")}
                          name="status"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <Autocomplete
                      id="valid"
                      disableClearable
                      value={formikStatusReasonObject.values.valid}
                      options={arrayValidityReason}
                      getOptionLabel={(option) => changeValidityName(option)}
                      onChange={(event, value) => {
                        formikStatusReasonObject.setFieldValue("valid", value);
                      }}
                      includeInputInList
                      renderOption={(p, option, state) => (
                        <li {...p} key={p.id}>
                          {changeValidityName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formikStatusReasonObject.touched.valid &&
                              formikStatusReasonObject.errors.valid
                          )}
                          fullWidth
                          helperText={
                            formikStatusReasonObject.touched.valid &&
                            formikStatusReasonObject.errors.valid
                          }
                          label={t("statusReason.validity")}
                          name="valid"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <Autocomplete
                      id="transferable"
                      disableClearable
                      value={formikStatusReasonObject.values.transferable}
                      options={arrayValidityReason}
                      getOptionLabel={(option) => changeTransferableName(option)}
                      onChange={(event, value) => {
                        formikStatusReasonObject.setFieldValue("transferable", value);
                      }}
                      includeInputInList
                      renderOption={(p, option, state) => (
                        <li {...p} key={p.id}>
                          {changeTransferableName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formikStatusReasonObject.touched.transferable &&
                              formikStatusReasonObject.errors.transferable
                          )}
                          fullWidth
                          helperText={
                            formikStatusReasonObject.touched.transferable &&
                            formikStatusReasonObject.errors.transferable
                          }
                          label={t("statusReason.transferable")}
                          name="transferable"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <Autocomplete
                      id="damages"
                      disableClearable
                      value={formikStatusReasonObject.values.damages}
                      options={arrayValidityReason}
                      getOptionLabel={(option) => changeTransferableName(option)}
                      onChange={(event, value) => {
                        formikStatusReasonObject.setFieldValue("damages", value);
                      }}
                      includeInputInList
                      renderOption={(p, option, state) => (
                        <li {...p} key={p.id}>
                          {changeTransferableName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formikStatusReasonObject.touched.damages &&
                              formikStatusReasonObject.errors.damages
                          )}
                          fullWidth
                          helperText={
                            formikStatusReasonObject.touched.damages &&
                            formikStatusReasonObject.errors.damages
                          }
                          label={t("statusReason.damages")}
                          name="damages"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* CR LPP-20500 RF-15 — attributo "Bloccato".
                    Sempre visibile (come Trasferibile/Danneggiabile).
                    Modificabile solo con grant SCOPE_AVAILABILITY_STATUS_REASON_MANAGE
                    e solo se lo status reason è NOT_AVAILABLE. */}
                <Grid xl={3} lg={3} md={6} xs={12} item>
                  <Box>
                    <Autocomplete
                      id="blocked"
                      disableClearable
                      disabled={!blockedEditable}
                      value={formikStatusReasonObject.values.blocked}
                      options={arrayValidityReason}
                      getOptionLabel={(option) => changeTransferableName(option)}
                      onChange={(event, value) => {
                        formikStatusReasonObject.setFieldValue("blocked", value);
                      }}
                      includeInputInList
                      renderOption={(p, option, state) => (
                        <li {...p} key={p.id}>
                          {changeTransferableName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formikStatusReasonObject.touched.blocked &&
                              formikStatusReasonObject.errors.blocked
                          )}
                          fullWidth
                          helperText={
                            (formikStatusReasonObject.touched.blocked &&
                              formikStatusReasonObject.errors.blocked) ||
                            (props.getStatusReasonDataForm.id &&
                            formikStatusReasonObject.values.status !== "NOT_AVAILABLE"
                              ? t(
                                  "statusReason.blockedOnlyNotAvailable",
                                  "Modificabile solo per status reason NOT AVAILABLE"
                                )
                              : "")
                          }
                          label={t("statusReason.blocked")}
                          name="blocked"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid xs="auto" item></Grid>
              </Grid>

              {toggleError && (
                <Box sx={{ mt: 2, color: "#e53935", fontSize: 12 }}>{toggleError}</Box>
              )}
            </DialogContent>
            <DialogActions>
              <Stack direction="row" spacing={2.875} p={1} mr={3.625}>
                <Button variant="outlined" size="medium" onClick={handleClose}>
                  <Typography
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 700,
                      lineHeight: 2.25,
                      color: "#C8017D",
                    }}
                    variant="subtitle1"
                  >
                    {t("actionButtons.cancel")}
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  type={"submit"}
                  form="statusReasonForm"
                  color="accent"
                >
                  <Typography
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 700,
                      lineHeight: 2.25,
                      color: "white",
                    }}
                    variant="subtitle1"
                  >
                    {t("actionButtons.save")}
                  </Typography>
                </Button>
              </Stack>
            </DialogActions>
          </Dialog>
        </form>
      </Box>

      {/* CR LPP-20500 RF-13 — popup conferma toggle status reason */}
      <Dialog open={impactDialogOpen} onClose={onCancelToggle}>
        <DialogTitle>
          {t("statusReason.toggle.confirmTitle", "Conferma cambio stato")}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 460 }}>
          {impactInfo && pendingToggleValues && (
            <Typography sx={{ mb: 1 }}>
              {t("statusReason.toggle.confirmMessage", {
                defaultValue:
                  "Stai per cambiare la causale {{code}} da {{from}} a {{to}}. Saranno impattati {{count}} veicoli. Confermi?",
                code: pendingToggleValues.code,
                from: changeStatusName(impactInfo.currentStatus),
                to: changeStatusName(impactInfo.targetStatus),
                count: impactCount ?? 0,
              })}
            </Typography>
          )}
          {toggleError && (
            <Box sx={{ mt: 1, color: "#e53935", fontSize: 12 }}>{toggleError}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelToggle} disabled={toggleLoading}>
            {t("actionButtons.cancel")}
          </Button>
          <Button
            onClick={onConfirmToggle}
            disabled={toggleLoading}
            variant="contained"
            color="accent"
          >
            {toggleLoading
              ? t("actionButtons.loading", "Attendere...")
              : t("actionButtons.confirm", "Conferma")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CR LPP-20500 RF-13 — popup esito operazione toggle (successo/errore) */}
      <Dialog open={resultDialogOpen} onClose={onCloseResult}>
        <DialogTitle sx={{ color: resultSuccess ? "#2e7d32" : "#e53935" }}>
          {resultSuccess
            ? t("statusReason.toggle.resultSuccessTitle", "Operazione completata")
            : t("statusReason.toggle.resultErrorTitle", "Operazione non riuscita")}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 420 }}>
          <Typography>{resultMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseResult} variant="contained" color="accent">
            {t("actionButtons.close", "Chiudi")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
